import { useEffect, useRef, useState } from "react";
import vwr, { type VwrInstance, type VwrOptions } from "vwr.js";

type VwrErrorEvent = Parameters<NonNullable<VwrOptions["onError"]>>[0];
import { FileWarning } from "lucide-react";

interface DocumentViewerProps {
  /** BookingFile id — the URL is resolved via /file_url/:id */
  fileId: string;
  /** Optional explicit height override. Default: 640px. */
  height?: string;
  className?: string;
}

/**
 * Embeds a vwr.js viewer for a BookingFile. Fetches a presigned URL
 * from `/file_url/:id`, then mounts the viewer into a portal-styled
 * shell (paper-hi background, hairline border, shadow-card).
 */
export function DocumentViewer({
  fileId,
  height = "640px",
  className = "",
}: DocumentViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<VwrInstance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fileId || !containerRef.current) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    if (viewerRef.current) {
      try {
        viewerRef.current.destroy();
      } catch {
        /* ignore */
      }
      viewerRef.current = null;
    }
    if (containerRef.current) containerRef.current.innerHTML = "";

    const loadDocument = async () => {
      try {
        const response = await fetch(`/file_url/${fileId}`, {
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error(
            response.status === 404
              ? "File not found"
              : "Couldn't get a download URL",
          );
        }
        const data = (await response.json()) as { url: string };
        if (cancelled || !containerRef.current) return;

        viewerRef.current = vwr.render(containerRef.current, data.url, {
          onLoad: () => {
            if (!cancelled) setIsLoading(false);
          },
          onError: (err: VwrErrorEvent) => {
            if (cancelled) return;
            setError(err.message || "Failed to load document");
            setIsLoading(false);
          },
        });

        // Belt-and-suspenders: hide the loader after 2s in case onLoad
        // doesn't fire (some renderers don't surface a clear "ready").
        setTimeout(() => {
          if (!cancelled) setIsLoading(false);
        }, 2000);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Failed to load document",
        );
        setIsLoading(false);
      }
    };

    loadDocument();

    return () => {
      cancelled = true;
      if (viewerRef.current) {
        try {
          viewerRef.current.destroy();
        } catch {
          /* ignore */
        }
        viewerRef.current = null;
      }
    };
  }, [fileId]);

  if (error) {
    return (
      <div
        className={`bg-paper-hi rounded-lg shadow-card flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="text-center px-6 max-w-sm">
          <div className="w-10 h-10 rounded-full bg-rust-s/60 mx-auto flex items-center justify-center text-rust mb-3">
            <FileWarning size={18} strokeWidth={1.8} />
          </div>
          <p className="text-[14px] font-medium text-ink-2">
            Couldn't load this document
          </p>
          <p className="text-[12px] text-ink-4 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative bg-paper-hi rounded-lg shadow-card overflow-hidden ${className}`}
      style={{ height }}
    >
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-paper-hi/90 backdrop-blur-sm">
          <span className="text-[12.5px] text-ink-4">Loading document…</span>
        </div>
      )}
      <div
        ref={containerRef}
        className={`w-full h-full transition-opacity duration-300 ${
          isLoading ? "opacity-0" : "opacity-100"
        }`}
      />
    </div>
  );
}
